import {
  alpha,
  Box,
  ClickAwayListener,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { ManualEffort } from "~/operations";
import { memo } from "react";

interface TopActionsEffortTooltipProps {
  isInitiative?: boolean;
  tooltipOpen: boolean;
  handleClose: () => void;
  handleClick: (event: React.MouseEvent) => void;
  children: React.ReactNode;
  effort: {
    mondooEffort: number;
    manualEffort: ManualEffort;
  };
}

const toHoursLabel = (minutes: number): string => {
  const hours = minutes / 60;
  const isInt = Number.isInteger(hours);
  return `${isInt ? hours : hours.toFixed(1)} h`;
};

const toHours1Decimal = (minutes: number): string => {
  return (minutes / 60).toFixed(1);
};

export const TopActionsEffortTooltip = memo(function TopActionsEffortTooltip({
  isInitiative,
  tooltipOpen,
  handleClose,
  handleClick,
  children,
  effort,
}: TopActionsEffortTooltipProps) {
  const manualBreakdown: { label: string; minutes: number }[] = [
    { label: "Research & Planning", minutes: effort.manualEffort.research },
    { label: "Scripting & Testing", minutes: effort.manualEffort.scripting },
    { label: "Staged Rollout", minutes: effort.manualEffort.rollout },
    { label: "Verification", minutes: effort.manualEffort.verification },
  ];

  const headlineStyles = {
    color: "text.primary",
    fontWeight: 700,
    mb: 1.2,
  } as const;

  const totalManualMinutes = effort.manualEffort.totalManualEffort;
  const totalMondooMinutes = effort.mondooEffort;
  const fasterPercent = Math.max(
    0,
    Math.round(
      (1 - totalMondooMinutes / Math.max(totalManualMinutes, 1)) * 100,
    ),
  );

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Tooltip
        title={
          <Box onClick={(e) => e.stopPropagation()}>
            <Typography component="p" variant="caption" sx={headlineStyles}>
              {isInitiative
                ? "Initiative effort estimate"
                : "Finding effort estimate"}
            </Typography>
            <Stack sx={{ width: 1, gap: 0.2, mb: 1.2 }}>
              <Box
                sx={{
                  width: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "4px 4px 0 0",
                  backgroundColor: (theme) =>
                    alpha(theme.palette.text.primary, 0.08),
                }}
              >
                <Typography
                  sx={{ fontSize: 10, fontWeight: 400, color: "text.primary" }}
                >
                  Manual
                </Typography>
                <Typography sx={{ fontSize: 10, fontWeight: 700 }}>
                  {toHoursLabel(totalManualMinutes)}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "0 0 4px 4px",
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.14),
                  color: (theme) =>
                    theme.palette.mode === "dark"
                      ? "text.secondary"
                      : "primary.darker",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 400,
                  }}
                >
                  Mondoo - {fasterPercent}% faster
                </Typography>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                >
                  {toHoursLabel(totalMondooMinutes)}
                </Typography>
              </Box>
            </Stack>
            <Box
              role="table"
              aria-label="Manual effort breakdown"
              sx={{ width: 1, my: 0.5 }}
            >
              <Stack
                direction="row"
                sx={{
                  width: 1,
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.625,
                }}
                role="row"
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 400,
                  }}
                  role="columnheader"
                >
                  TASK
                </Typography>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 400,
                  }}
                  role="columnheader"
                >
                  HOURS
                </Typography>
              </Stack>
              <Stack sx={{ width: 1, gap: 0.625 }}>
                {manualBreakdown.map((item) => (
                  <Stack
                    key={item.label}
                    direction="row"
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                    role="row"
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 500,
                        color: "text.primary",
                      }}
                      role="cell"
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 400,
                        color: "text.primary",
                      }}
                      role="cell"
                    >
                      {toHours1Decimal(item.minutes)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Box>
        }
        slotProps={{
          tooltip: {
            sx: {
              backgroundColor: "background.paper",
              p: 1.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: (theme) => theme.shadows[6],
              width: 200,
            },
          },
          arrow: {
            sx: {
              "&::before": {
                backgroundColor: "background.paper",
              },
            },
          },
        }}
        arrow
        open={tooltipOpen}
        disableHoverListener
        placement={"bottom-end"}
      >
        <span onClick={handleClick}>{children}</span>
      </Tooltip>
    </ClickAwayListener>
  );
});
